import React, { useCallback } from 'react';

import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import Button from '@atlaskit/button/new';

import type { TrackType } from '../../../../api/cc-smarts/models/TrackType';
import type { ActiveContent } from '../../../../types/activeContent';
import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useFlags } from '../../../context/FlagsProviderContext';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { useContextMenuActions } from '../../../hooks/useContextMenuActions';
import { setSupportingContent } from '../../../state/AudioPlayerState';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

import type { RefetchAudioButtonQuery } from './__generated__/RefetchAudioButtonQuery.graphql';

const messages = defineMessages({
	errorTitle: {
		id: 'ai-audio.audio-player.refetch-audio-button.error-title',
		defaultMessage: "Audio player didn't update",
		description: 'Error title shown when audio player fails to update',
	},
	errorDescription: {
		id: 'ai-audio.audio-player.refetch-audio-button.error-description',
		defaultMessage: 'Try making the changes again.',
		description: 'Error description shown when audio player fails to update',
	},
});

export type RefetchAudioButtonProps = {
	buttonText: MessageDescriptor;
	source: 'customizePanel' | 'sourcesPanel';
	shouldRequeue?: boolean;
	supportingContent?: ActiveContent['supportingContent'];
	isDisabled?: boolean;
};

/**
 * Button component that handles refetching audio with optional supporting content updates
 * and playlist management.
 */
export const RefetchAudioButton = ({
	buttonText,
	shouldRequeue = false,
	supportingContent = [],
	isDisabled = false,
}: RefetchAudioButtonProps) => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { cloudId, activeContent, setActiveMenu, activeFeature } = useAudioPlayerStore();
	const environment = useRelayEnvironment();
	const { removeTrack, addTrack } = useContextMenuActions(cloudId);
	const isLoading = streamingState.state === AudioStreamingState.LOADING;
	const { formatMessage } = useIntl();
	const flags = useFlags();

	const startNewAudio = useCallback(
		(overrideSupportingContent?: string[]) => {
			// TODO: Add analytics
			fetchAudio(overrideSupportingContent);
		},
		[fetchAudio],
	);

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	/** Handles requeuing the current track with updated supporting content */
	const handleRequeueTrack = useCallback(async () => {
		try {
			const playlist = await fetchQuery<RefetchAudioButtonQuery>(
				environment,
				graphql`
					query RefetchAudioButtonQuery($cloudId: ID!) {
						confluence_getPlaylist(cloudId: $cloudId) {
							tracks {
								nodes {
									mainSource
									supportingSources
									type
									mainContent {
										id
									}
								}
							}
						}
					}
				`,
				{ cloudId },
				{ fetchPolicy: 'store-or-network' },
			).toPromise();

			if (playlist?.confluence_getPlaylist?.tracks?.nodes) {
				const activeTrackType = activeFeature.toUpperCase() as TrackType;
				const currentTrackIndex = playlist.confluence_getPlaylist.tracks.nodes.findIndex(
					(node) =>
						node?.mainContent?.id === activeContent.contentId && node?.type === activeTrackType,
				);

				if (currentTrackIndex !== -1) {
					const currentTrack = playlist.confluence_getPlaylist.tracks.nodes[currentTrackIndex];
					const trackCopy = { ...currentTrack };

					// TODO: Create a new mutation that both adds and removes the track
					// to better guard against scenarios where removal succeeds but addition fails
					await removeTrack(
						currentTrack.mainSource,
						[...(currentTrack.supportingSources ?? [])],
						currentTrack.type as TrackType,
					);

					await addTrack(
						trackCopy.mainSource,
						supportingContent.map((resource) => resource.ari),
						trackCopy.type as TrackType,
						{
							showFlags: false,
							top: true,
						},
					);
				}
			}

			// Update global source state only once after successful BE requeue (if applicable),
			// preventing any potential inconsistencies between the BE and local player
			setSupportingContent(supportingContent);
			startNewAudio(supportingContent.map((resource) => resource.ari));
		} catch (error) {
			if (flags) {
				flags.showFlag({
					title: formatMessage(messages.errorTitle),
					description: formatMessage(messages.errorDescription),
					type: 'error',
					close: 'auto',
				});
			}
		}
	}, [
		cloudId,
		environment,
		removeTrack,
		addTrack,
		activeContent,
		activeFeature,
		supportingContent,
		flags,
		formatMessage,
		startNewAudio,
	]);

	const handleClick = useCallback(async () => {
		stopExistingAudio();

		if (shouldRequeue) {
			await handleRequeueTrack();
		} else {
			if (supportingContent.length > 0) {
				setSupportingContent(supportingContent);
			}

			// Pass the latest supporting content directly to fetchAudio
			const latestSupportingARIs = supportingContent.map((resource) => resource.ari);
			startNewAudio(latestSupportingARIs.length > 0 ? latestSupportingARIs : undefined);
			// Return to the audio player home screen
			setActiveMenu(undefined);
		}
	}, [
		startNewAudio,
		stopExistingAudio,
		handleRequeueTrack,
		shouldRequeue,
		supportingContent,
		setActiveMenu,
	]);

	return (
		<Button
			testId="refetch-audio-button"
			shouldFitContainer
			appearance="primary"
			onClick={handleClick}
			isLoading={isLoading}
			isDisabled={isDisabled}
		>
			{formatMessage(buttonText)}
		</Button>
	);
};
